import { Checkbox } from "components/Checkbox/Checkbox"
import {
	Form,
	type FormInstance,
	type FormItemProps,
} from "components/Form/Form"
import { Input } from "components/Input/Input"
import { Radio } from "components/Radio/Radio"
import {
	TemplatedFormParamsAnswer,
	TemplatedFormQuestion,
	TemplatedFormQuestionEnum,
	TemplateFormQuestionOption,
} from "interface/templatedForm"
import { ReactNode } from "react"
import { cn } from "styles/cn"

const { singleChoice, multipleChoice, text } = TemplatedFormQuestionEnum

interface TemplatedFormProps {
	answers?: TemplatedFormParamsAnswer[] | null
	questions: TemplatedFormQuestion[]
	description?: string
	form?: FormInstance
	explainPlaceholder?: string
	children?: ReactNode | ((form: FormInstance) => ReactNode)
	onFinish: (answers: TemplatedFormParamsAnswer[]) => void
}

export const TemplatedForm = (props: TemplatedFormProps) => {
	const [form] = Form.useForm(props.form)
	const handleOnFinish = (values: any) => {
		const answers = props.questions.map((question, questionIndex) => {
			const answer = values[computeQuestionName(questionIndex)]
			let selectOptions
			if (question.type === singleChoice || question.type === multipleChoice) {
				const options = Array.isArray(answer) ? answer : [answer]
				selectOptions = options.map((optionText: string) => {
					const explanation =
						values[computeOptionName(questionIndex, optionText)]
					return { option: optionText, explain: explanation || "" }
				})
			}
			return {
				questionText: question.questionText,
				textAnswer: question.type === text ? answer : "",
				selectOptions,
			}
		})

		props.onFinish(answers)
	}

	return (
		<>
			{props.description && <p className="mb-4 text-lg">{props.description}</p>}

			<Form
				form={form}
				layout="vertical"
				// requiredMark={false}
				onFinish={handleOnFinish}
				className="flex flex-col gap-2"
			>
				{props.questions.map((question, index) => {
					const { questionText, required, type, options } = question
					const name = computeQuestionName(index)
					const formItemProps: FormItemProps = {
						name,
						layout: "vertical",
						label: questionText,
						rules: [{ required, message: "This field is required" }],
						className: cn(
							"mb-4 w-full [&_.ant-form-item-label>label]:text-lg",
							"[&_.ant-form-item-required]:items-start [&_.ant-form-item-required]:before:my-1 [&_.ant-form-item-required]:before:content-['*']",
						),
					}
					const initialAnswer = props.answers?.find(
						(answer) => answer.questionText === questionText,
					)
					const selectOptions = initialAnswer?.selectOptions ?? []

					switch (type) {
						case singleChoice:
							const { option, explain } = selectOptions[0] ?? {
								option: undefined,
								explain: undefined,
							}

							return (
								<Form.Item key={name} {...formItemProps} initialValue={option}>
									<Radio.Group className="w-full">
										{options?.map((option, optIndex) => (
											<OptionWithExplain
												key={`option-${index}-${optIndex}`}
												type="radio"
												option={option}
												questionIndex={index}
												placeholder={props.explainPlaceholder}
												explain={explain}
											/>
										))}
									</Radio.Group>
								</Form.Item>
							)

						case multipleChoice:
							const initialValue = (selectOptions ?? []).map(
								({ option }) => option,
							)

							return (
								<Form.Item
									key={name}
									{...formItemProps}
									initialValue={initialValue}
								>
									<Checkbox.Group>
										{options?.map((option, optIndex) => {
											const { explain } =
												(selectOptions ?? []).find(
													(selectedOption) =>
														selectedOption.option === option.text,
												) ?? {}

											return (
												<OptionWithExplain
													key={`option-${index}-${optIndex}`}
													type="checkbox"
													option={option}
													questionIndex={index}
													placeholder={props.explainPlaceholder}
													explain={explain}
												/>
											)
										})}
									</Checkbox.Group>
								</Form.Item>
							)

						case text:
							return (
								<Form.Item key={name} {...formItemProps}>
									<Input />
								</Form.Item>
							)

						default:
							return null
					}
				})}

				{typeof props.children === "function"
					? props.children(form)
					: props.children}
			</Form>
		</>
	)
}

const OptionWithExplain = ({
	option,
	questionIndex,
	type,
	placeholder = "Please explain...",
	explain,
}: {
	option: TemplateFormQuestionOption
	questionIndex: number
	type: "radio" | "checkbox"
	placeholder?: string
	explain?: string
}) => {
	return (
		<div className="group m-0 mb-2 flex w-full flex-col flex-wrap p-0">
			{type === "radio" ? (
				<Radio value={option.text}>{option.text}</Radio>
			) : (
				<Checkbox value={option.text}>{option.text}</Checkbox>
			)}

			{option.requiredExplain && (
				<Form.Item
					className={cn(
						"m-0 mt-2 hidden w-full [flex:1] group-has-checked:block",
						(!explain || explain.length === 0) &&
							"[&_.ant-input-status-error]:border-red-20",
					)}
					name={computeOptionName(questionIndex, option.text)}
					initialValue={explain}
					rules={[
						({ getFieldValue }) => ({
							validator(_: any, value: string) {
								const selectedOptions =
									getFieldValue(computeQuestionName(questionIndex)) ||
									(type === "checkbox" ? [] : "")
								const isSelected =
									type === "radio"
										? selectedOptions === option.text
										: selectedOptions.includes(option.text)
								return isSelected && !value
									? Promise.reject(new Error("This field is required"))
									: Promise.resolve()
							},
						}),
					]}
				>
					<Input.TextArea
						className="w-full"
						placeholder={placeholder}
						autoSize={{ minRows: 1, maxRows: 5 }}
					/>
				</Form.Item>
			)}
		</div>
	)
}

const computeQuestionName = (questionIndex: number) =>
	`question_${questionIndex}`

const computeOptionName = (questionIndex: number, optionText: string) =>
	`explain_${questionIndex}_${optionText}`
